// Cull the splats a FOREIGN clip breaks, before they reach the rasterizer.
// Notes: ~/comments/aos-threejs-poc/drivenGaussians_sanitizeDrivenGaussians_why_a_cull.md

/** The SoA fields a driven lift produces. */
export interface DrivenGaussians {
  positions: Float32Array;
  scales: Float32Array;
  rotations: Float32Array;
  opacities: Float32Array;
  count: number;
}

/** Above this, one splat is a screen-wide smear rather than a surface. */
export const MAX_DRIVEN_SCALE = 0.15;

const finite3 = (a: Float32Array, i: number): boolean =>
  Number.isFinite(a[i]) && Number.isFinite(a[i + 1]) && Number.isFinite(a[i + 2]);

/**
 * Zero the opacity and collapse every non-finite or over-stretched splat, in place.
 *
 * The COUNT is deliberately unchanged: the packed slot layout is fixed for the session,
 * so a cull that removed entries would shift every later branch's range and tear Spark's
 * async depth sort. A culled splat stays in the list and draws nothing.
 *
 * @returns how many were culled — 0 on a clean frame.
 */
export function sanitizeDrivenGaussians(g: DrivenGaussians, maxScale = MAX_DRIVEN_SCALE): number {
  const { positions, scales, rotations, opacities, count } = g;
  let culled = 0;
  for (let i = 0; i < count; i++) {
    const p = i * 3;
    const r = i * 4;
    const bad = !finite3(positions, p) || !finite3(scales, p)
      || !Number.isFinite(rotations[r]) || !Number.isFinite(rotations[r + 1])
      || !Number.isFinite(rotations[r + 2]) || !Number.isFinite(rotations[r + 3])
      || scales[p] > maxScale || scales[p + 1] > maxScale || scales[p + 2] > maxScale;
    if (!bad) continue;
    culled++;
    opacities[i] = 0;
    positions[p] = 0; positions[p + 1] = 0; positions[p + 2] = 0;
    scales[p] = 1e-6; scales[p + 1] = 1e-6; scales[p + 2] = 1e-6;
    rotations[r] = 0; rotations[r + 1] = 0; rotations[r + 2] = 0; rotations[r + 3] = 1;
  }
  return culled;
}

/** Total culled across the branches — the number the debug global reports.
 *
 *  A branch reports -1 for "not counted on this path": the packed lift culls in
 *  `lift_pass2.wgsl` by returning, so there is no count to report without an
 *  atomic readback, which is the very cost that move removed. -1 propagates
 *  rather than summing as 0, because 0 reads as a CLEAN frame and would say
 *  "nothing broke" about a frame nobody looked at. */
export function sumCulled(perBranch: readonly number[]): number {
  // NO branches is 0, not "not counted": there was nothing to cull and nothing
  // went unlooked-at. Only a non-empty set of branches that ALL declined to count
  // is unknown.
  if (!perBranch.length) return 0;
  let total = 0;
  let counted = false;
  for (const n of perBranch) {
    if (n < 0) continue;
    counted = true;
    total += n;
  }
  return counted ? total : -1;
}
